/**
 * Board kit (D-BANK-UX-02) — the semantic-interaction foundations the
 * production scoping board is contractually required to have:
 *
 *  - useMediaFlag / useReducedMotion — matchMedia-backed flags (reduced
 *    motion, constrained-width drawer behavior), listener-safe across the
 *    legacy addListener and modern addEventListener APIs;
 *  - useAnnouncer — a COMPONENT-SCOPED polite live region: announcements are
 *    local to the surface and leave the DOM with it, so stale content can
 *    never leak onto another route;
 *  - FocusTrapDialog — aria-modal dialog with a real focus trap, Escape
 *    dismissal, and focus restoration to the invoking control;
 *  - usePointerDrag — dependency-free pointer-drag engine (the repo has no
 *    DnD library; this stays within the existing dependency graph). Drag is
 *    an ENHANCEMENT: every operation it performs is equally reachable
 *    through the Move menu, so keyboard and touch users lose nothing.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// ============================================================================
// Media flags
// ============================================================================

type MediaQueryListLike = {
  matches: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
};

export function useMediaFlag(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query) as MediaQueryListLike;
    const onChange = (): void => setMatches(mql.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

export function useReducedMotion(): boolean {
  return useMediaFlag('(prefers-reduced-motion: reduce)');
}

// ============================================================================
// Component-scoped announcer
// ============================================================================

export interface AnnouncerHandle {
  announce: (message: string) => void;
  node: React.ReactElement;
}

export function useAnnouncer(testId: string): AnnouncerHandle {
  const [message, setMessage] = useState('');
  const announce = useCallback((next: string) => {
    setMessage((prev) => {
      if (prev !== next) return next;
      // Clear-then-set so repeating the same message still re-announces.
      window.setTimeout(() => setMessage(next), 30);
      return '';
    });
  }, []);
  const node = useMemo(
    () => (
      <div
        data-testid={testId}
        className="banking-sr-only"
        aria-live="polite"
        role="status"
      >
        {message}
      </div>
    ),
    [testId, message]
  );
  return { announce, node };
}

// ============================================================================
// Focus-trapped modal dialog
// ============================================================================

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface FocusTrapDialogProps {
  label: string;
  onClose: () => void;
  /** The control that opened the dialog — focus returns here on close. */
  restoreFocusTo?: HTMLElement | null;
  /** Style overrides so non-banking surfaces can reuse the trap without
   *  importing banking CSS. Defaults preserve the existing banking modal. */
  backdropClassName?: string;
  dialogClassName?: string;
  children: React.ReactNode;
}

export const FocusTrapDialog: React.FC<FocusTrapDialogProps> = ({
  label,
  onClose,
  restoreFocusTo,
  backdropClassName = 'banking-modal-backdrop',
  dialogClassName = 'banking-modal',
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(restoreFocusTo ?? null);

  useEffect(() => {
    restoreRef.current = restoreFocusTo ?? restoreRef.current;
  }, [restoreFocusTo]);

  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    return () => {
      restoreRef.current?.focus();
    };
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !ref.current) return;
      const focusables = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && active === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    },
    [onClose]
  );

  return (
    <div className={backdropClassName}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={dialogClassName}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </div>
  );
};

// ============================================================================
// Pointer-drag engine
// ============================================================================

export interface DragPayload {
  kind: 'card' | 'drawer';
  code: string;
  mappingId?: string;
  label: string;
}

export interface DragState {
  payload: DragPayload;
  x: number;
  y: number;
  overLane: string | null;
}

export interface PointerDragApi {
  drag: DragState | null;
  /** Spread onto a draggable element. */
  dragHandleProps: (payload: DragPayload) => {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  };
  /** Register a drop lane element (call with null to unregister). */
  registerLane: (lane: string, el: HTMLElement | null) => void;
}

export function usePointerDrag(
  onDrop: (payload: DragPayload, lane: string) => void,
  enabled: boolean
): PointerDragApi {
  const [drag, setDrag] = useState<DragState | null>(null);
  const lanesRef = useRef(new Map<string, HTMLElement>());
  const pendingRef = useRef<{ payload: DragPayload; startX: number; startY: number } | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  const registerLane = useCallback((lane: string, el: HTMLElement | null) => {
    if (el) lanesRef.current.set(lane, el);
    else lanesRef.current.delete(lane);
  }, []);

  const laneAt = useCallback((x: number, y: number): string | null => {
    for (const [lane, el] of lanesRef.current) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return lane;
    }
    return null;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onMove = (event: PointerEvent): void => {
      const pending = pendingRef.current;
      if (pending && !dragRef.current) {
        if (Math.hypot(event.clientX - pending.startX, event.clientY - pending.startY) < 6) return;
        setDrag({
          payload: pending.payload,
          x: event.clientX,
          y: event.clientY,
          overLane: laneAt(event.clientX, event.clientY),
        });
        return;
      }
      if (dragRef.current) {
        setDrag({
          ...dragRef.current,
          x: event.clientX,
          y: event.clientY,
          overLane: laneAt(event.clientX, event.clientY),
        });
      }
    };
    const finish = (commit: boolean, event?: PointerEvent): void => {
      const active = dragRef.current;
      pendingRef.current = null;
      if (active) {
        setDrag(null);
        if (commit && event) {
          const lane = laneAt(event.clientX, event.clientY);
          if (lane) onDrop(active.payload, lane);
        }
      }
    };
    const onUp = (event: PointerEvent): void => finish(true, event);
    const onCancel = (): void => finish(false);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && dragRef.current) finish(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled, laneAt, onDrop]);

  const dragHandleProps = useCallback(
    (payload: DragPayload) => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>): void => {
        if (!enabled || event.button !== 0) return;
        // Never hijack interactive children — buttons stay clickable.
        const target = event.target as HTMLElement;
        if (target.closest('button, a, textarea, input, select, [role="menu"]')) return;
        pendingRef.current = {
          payload,
          startX: event.clientX,
          startY: event.clientY,
        };
      },
    }),
    [enabled]
  );

  return { drag, dragHandleProps, registerLane };
}
